/**
 * Centralized PWA referral state (spec item 1).
 *
 * A single module owns the pending referral code so every screen, popup and
 * store link behaves the same way: codes live in localStorage (surviving
 * navigation and reloads) under the same key the signup flow already reads
 * (`referralCodeUsed`), and are cleared once accepted.
 */

// Backend issues 8-char uppercase codes (charset excludes I, O, 0, 1).
export const REFERRAL_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{8}$/;

const STORAGE_KEY = 'referralCodeUsed';

/** Normalize user/clipboard input; null when unusable. */
export const normalizeReferralCode = (input) => {
  if (!input) return null;
  const code = String(input).trim().toUpperCase();
  return REFERRAL_CODE_PATTERN.test(code) ? code : null;
};

/** Extract `TCMCode=ABC123` from pasted/clipboard text (case-insensitive). */
export const extractTcmCode = (text) => {
  if (!text) return null;
  const match = String(text).match(/TCMCode\s*=\s*([A-Za-z0-9]{1,16})/i);
  if (!match) return null;
  return normalizeReferralCode(match[1]);
};

export const getPendingReferralCode = () => {
  try {
    return normalizeReferralCode(localStorage.getItem(STORAGE_KEY));
  } catch (error) {
    console.warn('Referral storage read failed:', error);
    return null;
  }
};

export const savePendingReferralCode = (code) => {
  const normalized = normalizeReferralCode(code);
  if (!normalized) return null;
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
  } catch (error) {
    console.warn('Referral storage write failed:', error);
  }
  return normalized;
};

export const clearPendingReferralCode = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Referral storage clear failed:', error);
  }
};
